
import { useState, useEffect } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Process from './components/Process';
import BeforeAfter from './components/BeforeAfter';
import ServiceAreas from './components/ServiceAreas';
import FAQs from './components/FAQs';
import Contact from './components/Contact';
import Header from './components/Header';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${SITE_URL}/#business`,
  name: 'X-Treme Aqua Pressure Cleaning',
  description: 'Professional pressure washing in Miami. Driveways, roofs, patios, and more. Fast response, free quotes, 5-star results.',
  url: SITE_URL,
  telephone: '+13052793585',
  priceRange: '$$',
  image: `${SITE_URL}/og-image.jpg`,
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Miami',
    addressRegion: 'FL',
    postalCode: '33165',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 25.75,
    longitude: -80.35,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '5.0',
    reviewCount: '50',
  },
  areaServed: {
    '@type': 'City',
    name: 'Miami',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Pressure Washing Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'House / Exterior Wash' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Driveway & Walkway Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Patio & Pool Deck Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Roof Soft Wash' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Fence & Wall Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pavers & Concrete Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Gutter Brightening' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Trash Can Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Commercial Storefront Cleaning' } },
    ],
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Do you offer same-day service?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'When available, yes—we do our best to accommodate urgent requests. Call or text us for the fastest response, and we\'ll let you know our availability.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is it safe for my surfaces?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely. We choose the right cleaning method for each surface—whether it\'s high-pressure washing for concrete or soft washing for delicate materials like roofs and siding. Your property is in safe hands.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you use chemicals?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We use eco-friendly cleaners when needed for tough stains, mold, or algae. All products are safe for your family, pets, and landscaping. We always pre-treat and rinse thoroughly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What do you need for a quote?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Just your address or service area, what you want cleaned, and optionally some photos. The more details you provide, the more accurate your quote will be. We typically respond within a few hours.',
      },
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/#webpage`,
  url: SITE_URL,
  name: 'Pressure Washing Miami | X-Treme Aqua Pressure Cleaning',
  description: 'Professional pressure washing in Miami. Driveways, roofs, patios, and more. Fast response, free quotes, 5-star results. Call (305) 279-3585.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#business` },
  inLanguage: 'en-US',
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'X-Treme Aqua Pressure Cleaning',
  inLanguage: 'en-US',
};

function injectSchema(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    (el as HTMLScriptElement).type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function Home() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    injectSchema('schema-local-business', localBusinessSchema);
    injectSchema('schema-faq', faqSchema);
    injectSchema('schema-webpage', webPageSchema);
    injectSchema('schema-website', websiteSchema);
    return () => {
      ['schema-local-business', 'schema-faq', 'schema-webpage', 'schema-website'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header isScrolled={isScrolled} />
      <Hero />
      <Services />
      <About />
      <Process />
      <BeforeAfter />
      <ServiceAreas />
      <FAQs />
      <Contact />
      <Footer />

      {/* Mobile Sticky Call Button */}
      <a
        href="tel:3052793585"
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform duration-300 z-50 lg:hidden animate-pulse"
        aria-label="Call Now"
      >
        <i className="ri-phone-fill text-white text-2xl"></i>
      </a>
    </div>
  );
}
